import React, { useState, useEffect } from 'react';

import { Link } from 'react-router-dom';

import { artistImageUrlWithFallback } from 'utils/helpers';

import {
  CollapsibleDrawerTable,
  CollapsibleSectionTableHeading,
  CollapsibleRowBodyCell,
  RowActionIcons,
} from 'styles/table-helpers';

import EditableText from 'components/EditableText/EditableText';
import LabelActionIcon from 'components/EditableText/LabelActionIcon';

import { TableRow, TableCell, Avatar, Typography, Checkbox } from '@material-ui/core';

import { OpenInNew as OpenInNewIcon, AddBox as AddBoxIcon } from '@material-ui/icons';
import AppleArtistIdModal from './AppleArtistIdModal';

import * as API from 'api';
import { useMutation } from 'react-query';
const Parties = API.Parties();

const ArtistsTableRow = ({
  onSave,
  isSaving,
  full_name,
  asset_party_id,
  platform_id,
  user_group_id,
  group,
  image_url,
  is_required_release_artist,
}) => {
  const group_name = group?.name;

  const artist_image_url = artistImageUrlWithFallback(image_url, 'small');

  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [name, setName] = useState(full_name);
  const [isRequiredReleaseArtist, setIsRequiredReleaseArtist] = useState(is_required_release_artist);

  useEffect(() => {
    setIsRequiredReleaseArtist(is_required_release_artist);
  }, [is_required_release_artist]);

  const [spotifyId, setSpotifyId] = useState(platform_id?.spotify);
  const [appleId, setAppleId] = useState(platform_id?.apple);
  const [soundcloudId, setSoundcloudId] = useState(platform_id?.soundcloud);
  const [vevoChannelName, setVevoChannelName] = useState(platform_id?.vevo);

  // Apple ID Modal
  const [showAppleArtistIdModal, setShowAppleArtistIdModal] = useState(false);
  const handleOnCloseModal = () => setShowAppleArtistIdModal(false);
  const createAppleArtistIdMutation = useMutation(() => Parties.createNewAppleArtistId(asset_party_id), {});

  const resetFieldsToDbData = () => {
    setSpotifyId(platform_id?.spotify);
    setAppleId(platform_id?.apple);
    setSoundcloudId(platform_id?.soundcloud);
    setName(full_name);
    setIsRequiredReleaseArtist(is_required_release_artist);
  };

  const setAndSaveAppleId = async newAppleId => {
    setAppleId(newAppleId);

    await onSave({
      asset_party_id,
      data: {
        platform_id: {
          spotify: spotifyId,
          apple: newAppleId,
          soundcloud: soundcloudId,
        },
      },
    });
    setIsEditing(false);
    handleOnCloseModal();
  };

  const toggleIsExpanded = () => {
    if (isExpanded && isEditing) setIsEditing(false);
    setIsExpanded(prevState => !prevState);
  };

  // Action Button Handlers

  const handleOnCancel = () => {
    resetFieldsToDbData();
    setIsEditing(false);
  };

  const handleOnEdit = () => {
    if (!isExpanded) setIsExpanded(true);
    setIsEditing(true);
  };

  const handleOnSave = async () => {
    await onSave({
      asset_party_id,
      data: {
        full_name: name,
        platform_id: {
          spotify: spotifyId,
          apple: appleId,
          soundcloud: soundcloudId,
          vevo: vevoChannelName,
        },
        is_required_release_artist: isRequiredReleaseArtist,
      },
    });
    setIsEditing(false);
  };

  // External Links

  const openAppleArtistUrlInNewTab = id => {
    const url = `https://music.apple.com/artist/${id}`;
    const newTab = window.open(url, '_blank');
    newTab.focus();
  };

  const goToSpotifyArtistUrl = () => {
    window.location.href = spotifyId;
  };

  const goToSoundcloudArtistUrl = () => {
    window.location.href = `https://soundcloud.com/${soundcloudId}`;
  };

  const goToVevoChannel = () => {
    window.location.href = `https://www.youtube.com/@${vevoChannelName}`;
  };
  // Row Cells

  const avatarCell = (
    <TableCell>
      <Avatar alt="artist image" src={artist_image_url} />
    </TableCell>
  );

  const partyIdCell = (
    <TableCell>
      <Typography>{asset_party_id}</Typography>
    </TableCell>
  );

  const artistNameCell = (
    <TableCell>
      <EditableText
        value={isEditing ? name : `${name}`}
        onChange={e => setName(e.target.value)}
        isEditing={isEditing}
        InputProps={{
          disableUnderline: !isEditing,
          style: {
            marginTop: 0,
            fontSize: '0.875rem',
          },
        }}
      />
    </TableCell>
  );

  const groupNameLinkCell = (
    <TableCell>
      <Link to={`/users?groupId=${user_group_id}`}>{group_name}</Link>
    </TableCell>
  );

  const isRequiredReleaseArtistCell = (
    <TableCell>
      <Checkbox
        disabled={!isEditing}
        onClick={() => setIsRequiredReleaseArtist(prevState => !prevState)}
        checked={isRequiredReleaseArtist}
      />
    </TableCell>
  );

  const actionIconsCell = (
    <TableCell align="right">
      <RowActionIcons
        isSaving={isSaving}
        isEditing={isEditing}
        onEdit={handleOnEdit}
        onSave={handleOnSave}
        onCancel={handleOnCancel}
        isExpanded={isExpanded}
        toggleIsExpanded={toggleIsExpanded}
      />
    </TableCell>
  );

  // Collapsible Drawer Cells

  const spotifyIdTableCell = (
    <CollapsibleRowBodyCell>
      <EditableText
        value={spotifyId}
        onChange={e => setSpotifyId(e.target.value)}
        isEditing={isEditing}
        labelTitle="Spotify ID"
        labelActionIcon={
          spotifyId && !isEditing && <LabelActionIcon onClick={goToSpotifyArtistUrl} icon={<OpenInNewIcon />} />
        }
      />
    </CollapsibleRowBodyCell>
  );

  const appleIdTableCell = (
    <CollapsibleRowBodyCell colSpan={2}>
      <EditableText
        value={appleId}
        onChange={e => setAppleId(e.target.value)}
        isEditing={isEditing}
        labelTitle="Apple ID"
        labelActionIcon={
          isEditing ? null : appleId ? (
            <LabelActionIcon onClick={() => openAppleArtistUrlInNewTab(appleId)} icon={<OpenInNewIcon />} />
          ) : (
            <LabelActionIcon
              disabled={isEditing}
              onClick={() => setShowAppleArtistIdModal(true)}
              icon={<AddBoxIcon />}
            />
          )
        }
      />
    </CollapsibleRowBodyCell>
  );

  const soundcloudIdTableCell = (
    <CollapsibleRowBodyCell>
      <EditableText
        value={soundcloudId}
        onChange={e => setSoundcloudId(e.target.value)}
        isEditing={isEditing}
        labelTitle="SoundCloud ID"
        labelActionIcon={
          soundcloudId && !isEditing && <LabelActionIcon onClick={goToSoundcloudArtistUrl} icon={<OpenInNewIcon />} />
        }
      />
    </CollapsibleRowBodyCell>
  );

  const vevoChannelNameTableCell = (
    <CollapsibleRowBodyCell colSpan={3}>
      <EditableText
        value={vevoChannelName}
        onChange={e => setVevoChannelName(e.target.value)}
        isEditing={isEditing}
        labelTitle="Vevo Channel"
        labelActionIcon={
          vevoChannelName && !isEditing && <LabelActionIcon onClick={goToVevoChannel} icon={<OpenInNewIcon />} />
        }
      />
    </CollapsibleRowBodyCell>
  );

  // Table
  const emptyCell = <CollapsibleRowBodyCell />;
  const gridColumnWidths = ['10%', '5%', '30%', '30%', '15%', '5%'];

  return (
    <>
      <TableRow>
        {avatarCell}
        {partyIdCell}
        {artistNameCell}
        {groupNameLinkCell}
        {isRequiredReleaseArtistCell}
        {actionIconsCell}
      </TableRow>
      <CollapsibleDrawerTable isExpanded={isExpanded} columnWidths={gridColumnWidths}>
        <CollapsibleSectionTableHeading
          widthCount={gridColumnWidths.length}
          colStart={1}
          colSpan={3}
          heading="Platform IDs"
        />
        <TableRow>
          {appleIdTableCell}
          {spotifyIdTableCell}
          {soundcloudIdTableCell}
          {vevoChannelNameTableCell}
        </TableRow>
      </CollapsibleDrawerTable>
      <AppleArtistIdModal
        asset_party_id={asset_party_id}
        full_name={full_name}
        onClickSubmit={createAppleArtistIdMutation}
        isLoading={false}
        isOpen={showAppleArtistIdModal}
        onSaveAppleArtistId={setAndSaveAppleId}
        onClickAppleLink={openAppleArtistUrlInNewTab}
        onClose={handleOnCloseModal}
      />
    </>
  );
};

export default ArtistsTableRow;
